import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingDown, 
  Users, 
  Shield, 
  Clock, 
  DollarSign, 
  Target,
  Heart,
  CheckCircle,
  Building2,
  BarChart3,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const EmployerLanding = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-background to-green-50">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Heart className="h-8 w-8 text-primary" />
            <span className="text-2xl font-bold">healthscores.ai</span>
            <Badge variant="secondary" className="ml-2">For Employers</Badge>
          </div>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="outline">Employee Portal</Button>
            </Link>
            <Button>Get Demo</Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center max-w-5xl">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
            Strategic Cost-Containment Through Health Intelligence
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Turn fragmented medical records into actionable health insights. healthscores.ai identifies risks early, 
            reduces avoidable claims by 30%, and frees your employees from time-consuming caregiving tasks.
          </p>
          <div className="flex gap-4 justify-center mb-12">
            <Button size="lg" className="px-8">
              Schedule Demo
            </Button>
            <Button variant="outline" size="lg" className="px-8">
              View ROI Calculator
            </Button>
          </div>
          
          {/* Key Stats */}
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">30%</div>
              <div className="text-sm text-muted-foreground">Reduction in preventable claims</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">61%</div>
              <div className="text-sm text-muted-foreground">Of eldercare providers are also employees</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">6-8%</div>
              <div className="text-sm text-muted-foreground">Annual medical cost trend</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-red-700">The Challenge</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <TrendingDown className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold">Rising Healthcare Costs</h3>
                    <p className="text-muted-foreground">6-8% annual medical cost trend strains budgets</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold">Caregiver Burden</h3>
                    <p className="text-muted-foreground">61% of eldercare providers are also employees</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Target className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold">Preventable Conditions</h3>
                    <p className="text-muted-foreground">30%+ of spend on avoidable ER visits and chronic diseases</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 text-green-700">The Solution</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold">Early Risk Detection</h3>
                    <p className="text-muted-foreground">0-1000 Health Score identifies concerns before they become claims</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold">Care Circle Management</h3>
                    <p className="text-muted-foreground">Employees monitor dependents remotely, reducing time away</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold">Telemedicine Integration</h3>
                    <p className="text-muted-foreground">Surfaces Health Scores in existing provider sessions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Flexible Pricing for Every Organization</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Per Employee Per Month (PEPM) pricing that scales with your workforce
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="relative">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Core Platform
                </CardTitle>
                <div className="text-3xl font-bold">$3.00<span className="text-sm font-normal text-muted-foreground"> PEPM</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Telemedicine integration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Unlimited dependents & Care Circle</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Employer analytics dashboard</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">0-1000 Health Scores</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Chronic Disease Modules</CardTitle>
                <div className="text-3xl font-bold">+$2.00<span className="text-sm font-normal text-muted-foreground"> PEPM</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">Diabetes management</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">Hypertension monitoring</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">Mental health coaching</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">Personalized interventions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>White-Label Branding</CardTitle>
                <div className="text-3xl font-bold">+$0.50<span className="text-sm font-normal text-muted-foreground"> PEPM</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    <span className="text-sm">Custom company branding</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    <span className="text-sm">Branded mobile apps</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    <span className="text-sm">Custom domain</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    <span className="text-sm">Priority support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-8">
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Strategic Business Impact</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              More than a health platform - a strategic cost-containment tool
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center">
              <CardHeader>
                <DollarSign className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <CardTitle>Cost Reduction</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Identify high-risk employees early to prevent expensive urgent care visits and ER trips
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Users className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <CardTitle>Reduced Absenteeism</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Care Circle features help employee-caregivers manage dependents remotely, reducing time away
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <BarChart3 className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <CardTitle>Data-Driven Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Employer analytics dashboard provides population health trends and ROI metrics
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Building2 className="h-12 w-12 text-orange-600 mx-auto mb-4" />
                <CardTitle>Easy Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Works with your existing telemedicine provider - no disruption to current benefits
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Benefits Strategy?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join forward-thinking employers who are reducing healthcare costs while improving employee satisfaction.
          </p>
          <div className="flex gap-4 justify-center">
            <Button size="lg" variant="secondary" className="px-8">
              <Phone className="h-4 w-4 mr-2" />
              Schedule Demo
            </Button>
            <Button size="lg" variant="secondary" className="px-8">
              Download ROI Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 px-4 bg-background">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Heart className="h-6 w-6 text-primary" />
                <span className="text-lg font-bold">healthscores.ai</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Strategic health intelligence for forward-thinking employers.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Solutions</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Core Platform</li>
                <li>Chronic Disease Management</li>
                <li>Care Circle</li>
                <li>Analytics Dashboard</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Company</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>About Us</li>
                <li>Case Studies</li>
                <li>Partners</li>
                <li>Careers</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Contact</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Schedule Demo</li>
                <li>Sales: 1-800-HEALTH</li>
                <li>Support</li>
                <li>Security</li>
              </ul>
            </div>
          </div>
          <div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 healthscores.ai. All rights reserved. | HIPAA Compliant | SOC 2 Certified</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EmployerLanding;
